import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import BackNav from "@/components/BackNav";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import SeoAlternates from "@/components/SeoAlternates";
import { itemListJsonLd } from "@/lib/structuredData";
import { useMaterials } from "@/hooks/useBioNexusData";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import Reveal, { SplitWords } from "@/components/Reveal";
import MaterialCard from "@/components/cards/MaterialCard";
import { countMeasured } from "@/lib/materials";
import { cn } from "@/lib/utils";

const DATA_FILTERS = ["all", "complete", "sourced", "degradation", "tensile"] as const;

const Materials = () => {
  const { data: materials, isLoading } = useMaterials();
  const { m, locale } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const familyFilter = searchParams.get("family") || "all";
  const [dataFilter, setDataFilter] = useState<string>("all");

  const setFamilyFilter = (family: string) => {
    const next = new URLSearchParams(searchParams);
    if (family === "all") next.delete("family");
    else next.set("family", family);
    setSearchParams(next, { replace: true });
  };

  const families = [...new Set(materials?.map((m) => m.family).filter(Boolean) as string[] || [])];

  const filteredMaterials = materials?.filter((m) => {
    if (familyFilter !== "all" && m.family !== familyFilter) return false;
    if (dataFilter === "degradation" && !m.degradation_days && !m.degradation_value) return false;
    if (dataFilter === "tensile" && !m.tensile_strength_mpa) return false;
    if (dataFilter === "complete" && countMeasured(m) < 3) return false;
    if (dataFilter === "sourced" && !m.source_doi && !m.source_paper) return false;
    return true;
  }) || [];

  return (
    <div className="min-h-screen bg-background page-enter">
      <Helmet>
        <title>{`${m.meta.materialsTitle} — BioNexus`}</title>
        <meta name="description" content={m.meta.materialsDescription} />
        <meta property="og:title" content={`${m.meta.materialsTitle} — BioNexus`} />
        <meta property="og:description" content={m.meta.materialsDescription} />
        <meta property="og:type" content="website" />
        {materials && materials.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(itemListJsonLd(locale, "materials", materials.map((x) => ({ id: x.id, name: x.name }))))}
          </script>
        )}
      </Helmet>
      <SeoAlternates path="/materials" />
      <Header />

      <main className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-12">
            <BackNav to="/" label={m.common.backHome} />
          </div>

          {/* Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-7">
              <p className="text-sm text-muted-foreground mb-4">{m.materials.eyebrow}</p>
              <h1 className="font-display font-medium text-[3.6rem] md:text-5xl leading-[1.04] tracking-tight">
                <SplitWords text={m.materials.title} baseDelay={80} />
              </h1>
            </div>
            <p className="hero-fade lg:col-span-5 text-muted-foreground leading-relaxed" style={{ animationDelay: "400ms" }}>
              {m.materials.lead}
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between mb-12 pb-8 border-b border-border">
            <div className="-mx-6 px-6 md:mx-0 md:px-0 flex gap-2 overflow-x-auto scrollbar-hide">
              {["all", ...families].map((f) => {
                const active = familyFilter === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFamilyFilter(f)}
                    aria-pressed={active}
                    className={cn(
                      "inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-all duration-300",
                      active
                        ? "border-foreground bg-foreground text-background"
                        : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground",
                    )}
                  >
                    {f === "all" ? m.materials.allFamilies : <span lang="en">{f}</span>}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <select
                value={dataFilter}
                onChange={(e) => setDataFilter(e.target.value)}
                aria-label={m.materials.filterLabel}
                className="rounded-full text-sm bg-transparent border border-border px-4 py-2 focus:outline-none focus:border-primary"
              >
                {DATA_FILTERS.map((f) => (
                  <option key={f} value={f}>{m.materials.filters[f]}</option>
                ))}
              </select>
              {!isLoading && (
                <span className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
                  {m.materials.count(filteredMaterials.length)}
                </span>
              )}
            </div>
          </div>

          {/* Grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="rounded-xl border border-border overflow-hidden">
                  <div className="aspect-[4/3] bg-muted animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 w-1/3 bg-muted animate-pulse rounded" />
                    <div className="h-6 w-3/4 bg-muted animate-pulse rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredMaterials.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-muted-foreground mb-4">{m.materials.empty}</p>
              <button
                type="button"
                onClick={() => {
                  setFamilyFilter("all");
                  setDataFilter("all");
                }}
                className="font-mono text-xs uppercase tracking-[0.12em] text-primary"
              >
                {m.materials.clearFilters}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMaterials.map((material, idx) => (
                <Reveal key={material.id} delay={(idx % 3) * 80} className="h-full">
                  <MaterialCard material={material} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Materials;
